import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, CircularProgress, Paper, Typography } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import axios from "../../services";
import toaster from "../../utils/toaster";
import QuotationPricingSection, { getDefaultQuotationDocState } from "../QuotationPricingSection/QuotationPricingSection";
import QuotationPDF from "../QuotationPDF/QuotationPDF";
import {
  mapQuotationDocToPayload,
  itemsFromApiToPayload,
  computePayloadInventoryTotals,
} from "../../utils/quotationPricingPayload";
import { getPricingFormStateFromQuotation } from "../../utils/quotationPricingFormLoad";

const useStyles = makeStyles((theme) => ({
  introBox: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "130px",
    marginTop: "60px",
    fontFamily: "Roboto Mono, monospace",
  },
  primaryText: {
    fontSize: "35px",
    fontWeight: "bold",
  },
  secondaryText: {
    position: "relative",
    top: "-10px",
    opacity: ".78",
  },
  actions: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },
  sectionCard: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    width: "calc(100% - 190px)",
    maxWidth: 1200,
    padding: theme.spacing(2),
  },
  pricingFormWrap: {
    marginLeft: "130px",
    marginTop: theme.spacing(1),
    width: "calc(100% - 190px)",
    maxWidth: 1200,
    minWidth: 0,
    boxSizing: "border-box",
    /** Contain wide tables/fields so nothing paints under the sidebar */
    overflowX: "auto",
    paddingRight: theme.spacing(1),
  },
}));

function mapQuotationToBaseline(quotation, items) {
  return {
    customer: {
      customerName: quotation.customerName || "",
      email: quotation.email || "",
      phoneNumber: quotation.phoneNumber || "",
      companyName: quotation.companyName || "",
      preferredMovingDate: quotation.preferredMovingDate || "",
      originAddress1: quotation.originAddress1 || "",
      originAddress2: quotation.originAddress2 || "",
      originCity: quotation.originCity || "",
      originState: quotation.originState || "",
      originPincode: quotation.originPincode || "",
      destinationAddress1: quotation.destinationAddress1 || "",
      destinationAddress2: quotation.destinationAddress2 || "",
      destinationCity: quotation.destinationCity || "",
      destinationState: quotation.destinationState || "",
      destinationPincode: quotation.destinationPincode || "",
    },
    notes: quotation.notes || "",
    status: quotation.status || "draft",
    items: items || [],
    vehicles: Array.isArray(quotation.vehicles) && quotation.vehicles.length ? [...quotation.vehicles] : [""],
    importSourceFileName: quotation.importSourceFileName || "",
  };
}

const QuotationPricingPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { quotationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [quotationDoc, setQuotationDoc] = useState(() => getDefaultQuotationDocState());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`quotations/${quotationId}`);
        if (cancelled) return;
        const { quotation, items } = response.data;
        setBaseline(mapQuotationToBaseline(quotation, items));
        const { quotationDoc: doc } = getPricingFormStateFromQuotation(quotation);
        setQuotationDoc(doc);
      } catch (err) {
        toaster(err?.response?.data?.message || "Failed to load quotation", { type: "error" });
        history.push("/quotations");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [quotationId, history]);

  const savePricing = async () => {
    if (!baseline) return;
    if (!(quotationDoc.customerName || baseline.customer.customerName || "").trim()) {
      toaster("Customer name is required", { type: "error" });
      return;
    }
    const itemsPayload = itemsFromApiToPayload(baseline.items);
    const vehicleList = baseline.vehicles.map((v) => String(v).trim()).filter(Boolean);
    if (!itemsPayload.length && !vehicleList.length) {
      toaster("This quotation has no inventory lines or vehicles. Add them from Edit Quotation first.", {
        type: "error",
      });
      return;
    }
    const totals = computePayloadInventoryTotals(itemsPayload);
    const pricing = mapQuotationDocToPayload(quotationDoc);
    const payload = {
      ...baseline.customer,
      ...pricing,
      notes: baseline.notes,
      status: baseline.status,
      vehicles: vehicleList,
      items: itemsPayload,
      totalItemsCount: totals.totalItemsCount,
      totalQuantity: totals.totalQuantity,
      totalCft: pricing.totalCft != null ? pricing.totalCft : totals.totalCft,
      importSourceFileName: baseline.importSourceFileName,
    };
    setSaving(true);
    try {
      const res = await axios.put(`quotations/${quotationId}`, payload);
      toaster(res.data?.message || "Quotation pricing saved");
      const refreshed = await axios.get(`quotations/${quotationId}`);
      const { quotation: q2, items: items2 } = refreshed.data;
      setBaseline(mapQuotationToBaseline(q2, items2));
      setQuotationDoc(getPricingFormStateFromQuotation(q2).quotationDoc);
    } catch (err) {
      toaster(err?.response?.data?.message || "Failed to save quotation pricing", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await axios.get(`quotations/${quotationId}`);
      const blob = await pdf(<QuotationPDF data={response.data} />).toBlob();
      const q = response.data.quotation || {};
      const safeName = (q.customerName || "quotation").replace(/[^\w-]+/g, "_");
      saveAs(blob, `Quotation-${safeName}-${String(quotationId).slice(-6)}.pdf`);
      toaster("Quotation PDF downloaded");
    } catch (err) {
      console.error("Quotation PDF download:", err);
      const msg =
        err?.response?.data?.message || err?.message || "Failed to generate quotation PDF";
      toaster(msg, { type: "error" });
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading || !baseline) {
    return (
      <Box style={{ marginLeft: "130px", marginTop: "120px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const titleName = (quotationDoc.customerName || baseline.customer.customerName || "Quotation").trim();

  return (
    <Box>
      <Box className={classes.introBox}>
        <Typography variant="h3" component="h1" className={classes.primaryText}>
          Quotation pricing &amp; PDF
        </Typography>
        <Typography variant="h6" component="span" className={classes.secondaryText}>
          {titleName}
        </Typography>
      </Box>

      <Box className={classes.actions}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/quotations")}>
          Back to list
        </Button>
        <Button variant="outlined" onClick={() => history.push(`/quotations/${quotationId}/edit`)}>
          Edit quotation (items &amp; CFT)
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PictureAsPdfIcon />}
          onClick={downloadPdf}
          disabled={pdfLoading}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={savePricing}
          disabled={saving}
        >
          Save pricing
        </Button>
      </Box>

      <Paper className={classes.sectionCard} elevation={0}>
        <Typography variant="body2" color="textSecondary" paragraph style={{ marginBottom: 0 }}>
          Structured ACE quotation template: Ref no., customer name, and From/To match the PDF header. Charge lines
          follow the standard wording. Inventory comes from the main quotation CFT page.
        </Typography>
      </Paper>

      <Box className={classes.pricingFormWrap}>
        <QuotationPricingSection doc={quotationDoc} setDoc={setQuotationDoc} />
      </Box>
    </Box>
  );
};

export default QuotationPricingPage;
